<template>  
  <div class="modal-card-body">  
    <p class="has-text-centered" v-html="config.verbiage.get_measurements_paragraph"></p>
    <div class="container mt-5">
      <b-field label="Project Description">
          <b-input v-model="projectName" placeholder="Optional" :disabled="isDataSaved"></b-input>
      </b-field>
      <b-field grouped>
        <b-field expanded label="First Name" :message="firstName ? '' : 'First name is required'">
          <b-input v-model="firstName" required :disabled="isDataSaved"></b-input>
        </b-field>
        <b-field expanded label="Last Name" :message="lastName ? '' : 'Last name is required'">
          <b-input v-model="lastName" required :disabled="isDataSaved"></b-input>
        </b-field>
      </b-field>    
      <b-field grouped>
        <b-field expanded label="Email" :message="validEmail ? '' : 'Email is not valid'">
          <b-input v-model="email" type="email" required :disabled="isDataSaved"></b-input>
        </b-field>
        <b-field expanded label="Phone" :message="validPhone ? '' : 'Phone number is not valid'">
          <b-input v-model="phone" type="tel" required :disabled="isDataSaved"></b-input>
        </b-field>
      </b-field>    
      <b-field label="Turf product you're interested in">
        <b-select expanded v-model="productInterest" :disabled="isDataSaved">
          <option v-for="product in config.products" :value="product.value" :key="product.value">{{ product.label }}</option>
        </b-select>
      </b-field>

      <b-field position="is-centered">
        <b-checkbox :class="['terms', $mq]" v-model="agree" :disabled="isDataSaved"><span v-html="verbiage.get_measurements_privacy_policy_paragraph"></span></b-checkbox>
      </b-field>
      <b-field position="is-centered">
        <b-checkbox ref="tosCheckbox" :class="['terms', $mq]" v-model="tosAgree" :disabled="isDataSaved"><span v-html="verbiage.get_measurements_tos_checkbox_label"></span></b-checkbox>
      </b-field>
    </div>
  </div>   
</template>

<style>
  .terms.sm span.control-label {
    font-size: 0.8em;
  }

  div.modal-card-body {
    overflow: auto;
  } 
</style>

<script>
import { ref, computed, watch, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useMainStore } from '@/stores/main'
import { useMapSharingStore } from '@/stores/mapSharing'
import { useContactStore } from '@/stores/contact'
import { useDialogsStore } from '@/stores/dialogs'
import { useConfig } from '@/composables/useConfig'
import LeadWorkflowNavigationButtons from '@/components/lead_workflow_components/LeadWorkflowNavigationButtons.vue'

export default {
  name: 'ContactInfo',
  components: {
    LeadWorkflowNavigationButtons
  },
  props: {
    nextButtonClicked: {
      type: Boolean,
      default: false
    }
  },
  emits: ['set-title', 'set-prev-route', 'set-next-button-label', 'set-show-next', 'set-next-button-enabled'],
  setup(props, { emit }) {
    const router = useRouter()
    const mainStore = useMainStore()
    const mapSharingStore = useMapSharingStore()
    const contactStore = useContactStore()
    const dialogsStore = useDialogsStore()
    const { verbiage, config } = useConfig()

    const tosCheckbox = ref(null)
    const projectName = ref(mainStore.projectName || '')
    const firstName = ref(contactStore.firstName || '')
    const lastName = ref(contactStore.lastName || '')
    const email = ref(contactStore.email || '')
    const phone = ref(contactStore.phone || '')
    const productInterest = ref(contactStore.productInterest || '')
    const agree = ref(contactStore.agree || false)
    const tosAgree = ref(contactStore.tosAgree || false)

    // Computed
    const isFirstNameEntered = computed(() => firstName.value !== '')
    const isLastNameEntered = computed(() => lastName.value !== '')
    const isAgreeChecked = computed(() => agree.value)
    const isTosAgreeChecked = computed(() => tosAgree.value)
    const isProductInterestSelected = computed(() => productInterest.value !== '')

    const validEmail = computed(() => {
      const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      return re.test(String(email.value).toLowerCase())
    })

    const validPhone = computed(() => {
      const re = /^\+?(\d[\s-]?)?(\(\d{1,3}\)[\s-]?)?[\d\s-]{7,}\d$/
      return re.test(String(phone.value).toLowerCase())
    })

    const isDataSaved = computed(() => {
      return !!(contactStore.firstName && contactStore.lastName && contactStore.email)
    })

    const validFields = computed(() => {
      return (
        isFirstNameEntered.value &&
        isLastNameEntered.value &&
        isProductInterestSelected.value &&
        isAgreeChecked.value &&
        isTosAgreeChecked.value &&
        validEmail.value &&
        validPhone.value
      )
    })

    // Methods
    const shareMap = async () => {
      try {
        mainStore.setLoading(true)
        dialogsStore.snackbar('Saving your measurements...')
        mainStore.setProjectName(projectName.value)

        await mapSharingStore.shareMap()
      } catch (error) {
        dialogsStore.alert(0, `Could not process your measurements. ${error.message}`, {
          title: 'Error',
          type: 'is-error'
        })

        throw error
      } finally {
        mainStore.setLoading(false)
      }
    }

    const getMeasurements = async () => {
      if (isDataSaved.value && mapSharingStore.mapId) {
        router.push('/flow/results')
        return
      }

      contactStore.saveContactInfo({
        data: {
          projectName: projectName.value,
          firstName: firstName.value,
          lastName: lastName.value,
          email: email.value,
          phone: phone.value,
          productInterest: productInterest.value,
          agree: agree.value,
          tosAgree: tosAgree.value
        }
      })
      await shareMap()

      router.push('/flow/results')
    }

    // Watchers
    watch(validFields, (val) => {
      emit('set-next-button-enabled', val)
    }, { immediate: true })

    watch(() => props.nextButtonClicked, (val) => {
      if (val) {
        getMeasurements()
      }
    })

    // Lifecycle
    onMounted(() => {
      emit('set-title', verbiage.value.get_measurements_title)
      projectName.value = mainStore.projectName || ''
      firstName.value = contactStore.firstName || ''
      lastName.value = contactStore.lastName || ''
      email.value = contactStore.email || ''
      phone.value = contactStore.phone || ''
      productInterest.value = contactStore.productInterest || ''
      agree.value = contactStore.agree || false
      tosAgree.value = contactStore.tosAgree || false

      tosCheckbox.value.$el.querySelector('a.tos_link').addEventListener('click', () => {
        dialogsStore.alert(0, verbiage.value.get_measurements_tos_alert, {
          title: 'Terms of Service',
          type: 'is-info'
        })
      })

      emit('set-prev-route', '/flow/outline-areas')
      emit('set-next-button-label', 'GET MEASUREMENTS')
      emit('set-show-next', true)
    })

    return {
      tosCheckbox,
      projectName,
      firstName,
      lastName,
      email,
      phone,
      productInterest,
      agree,
      tosAgree,
      config,
      verbiage,
      isFirstNameEntered,
      isLastNameEntered,
      isAgreeChecked,
      isTosAgreeChecked,
      isProductInterestSelected,
      validEmail,
      validPhone,
      isDataSaved,
      validFields,
      getMeasurements
    }
  }
}
</script>

